import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipForward, Zap, Clock, ChevronRight, AlertTriangle, ArrowLeft } from 'lucide-react';
import { API } from '../App';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const MOMENT_COLORS = { phase: '#FF4D00', tension_peak: '#EF4444', hot_take: '#FF4D00', final_call: '#22C55E' };

export default function ReplayViewer() {
  const { frayId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [tension, setTension] = useState(0);
  const timerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    API.get(`/api/frays/${frayId}/replay`)
      .then(r => setData(r.data))
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [frayId, navigate]);

  const processEvent = useCallback((event) => {
    const type = event.event_type;
    const payload = event.payload || {};

    if (type === 'phase_transition') {
      setCurrentPhase(payload);
      setMessages(prev => [...prev, { type: 'phase', data: payload }]);
    } else if (type === 'voice_message' && payload.status === 'streaming') {
      setMessages(prev => [...prev, { type: 'message', data: payload }]);
    } else if (type === 'tension_update') {
      setTension(payload.score || 0);
    } else if (type === 'verdict') {
      setMessages(prev => [...prev, { type: 'verdict', data: payload }]);
    } else if (type === 'error') {
      setMessages(prev => [...prev, { type: 'error', data: payload }]);
    }
  }, []);

  // Playback engine
  useEffect(() => {
    if (!playing || !data?.events) return;

    const interval = 200 / speed; // Base interval, adjusted by speed
    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        if (prev >= data.events.length) {
          setPlaying(false);
          return prev;
        }
        processEvent(data.events[prev]);
        return prev + 1;
      });
    }, interval);

    return () => clearInterval(timerRef.current);
  }, [playing, speed, data, processEvent]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const jumpToMoment = (seq) => {
    setPlaying(false);
    setMessages([]);
    setCurrentPhase(null);
    setTension(0);

    const events = data?.events || [];
    const newMessages = [];
    let newPhase = null;
    let newTension = 0;

    for (const ev of events) {
      if (ev.sequence_id > seq) break;
      const type = ev.event_type;
      const payload = ev.payload || {};
      if (type === 'phase_transition') {
        newPhase = payload;
        newMessages.push({ type: 'phase', data: payload });
      } else if (type === 'voice_message' && payload.status === 'streaming') {
        newMessages.push({ type: 'message', data: payload });
      } else if (type === 'tension_update') {
        newTension = payload.score || 0;
      } else if (type === 'verdict') {
        newMessages.push({ type: 'verdict', data: payload });
      }
    }

    setMessages(newMessages);
    setCurrentPhase(newPhase);
    setTension(newTension);
    setCurrentIdx(events.findIndex(e => e.sequence_id > seq));
  };

  const reset = () => {
    setPlaying(false);
    setCurrentIdx(0);
    setMessages([]);
    setCurrentPhase(null);
    setTension(0);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;
  const voices = data.voices || [];
  const progress = data.events?.length ? (currentIdx / data.events.length) * 100 : 0;

  return (
    <div className="h-screen bg-[#0F0F0F] flex flex-col overflow-hidden" data-testid="replay-viewer">
      {/* Header */}
      <div className="h-12 glass border-b border-[#2A2A2A] flex items-center justify-between px-3 md:px-4 shrink-0">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <Link to={`/fray/${frayId}/results`} className="text-[#A0A0A0] hover:text-[#FF4D00] min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="px-2 py-0.5 bg-[#A855F7]/10 border border-[#A855F7]/30 text-[#A855F7] text-[9px] font-mono shrink-0">REPLAY</div>
          <span className="text-sm font-['Barlow_Condensed'] font-medium truncate">{data.template_name}</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          {data.tension_score > 0 && (
            <span className="text-xs font-mono text-[#FF4D00]"><Zap className="w-3 h-3 inline mr-1" />{data.tension_score}%</span>
          )}
          {data.verdict?.verdict && (
            <span className="text-xs font-mono" style={{ color: data.verdict.verdict === 'Pass' ? '#22C55E' : data.verdict.verdict === 'Fail' ? '#EF4444' : '#F59E0B' }}>
              {data.verdict.verdict}
            </span>
          )}
        </div>
      </div>

      {/* Voice Rail */}
      <div className="h-14 md:h-16 border-b border-[#2A2A2A] flex items-center gap-2 md:gap-3 px-3 md:px-4 overflow-x-auto shrink-0">
        {voices.map((voice, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-1.5 border border-[#2A2A2A] shrink-0">
            <div className="w-6 h-6 flex items-center justify-center border text-[9px] font-bold font-['Barlow_Condensed']"
              style={{ borderColor: voice.color, color: voice.color }}>{voice.name.charAt(0)}</div>
            <span className="text-[10px] font-mono text-[#A0A0A0] truncate max-w-[80px]">{voice.name}</span>
          </div>
        ))}
      </div>

      {/* Message Area */}
      <div className="flex-1 overflow-y-auto px-3 md:px-6 py-4" data-testid="replay-messages">
        {messages.length === 0 && !playing && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Play className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
              <p className="text-[#A0A0A0] text-sm">Press play to start the replay</p>
              <p className="text-xs text-[#2A2A2A] mt-1 font-mono">{data.total_events} events, {data.moments?.length || 0} key moments</p>
            </div>
          </div>
        )}

        <AnimatePresence>
          {messages.map((msg, i) => {
            if (msg.type === 'phase') {
              return (
                <motion.div key={`p-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="my-6 text-center">
                  <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#1A1A1A] border border-[#FF4D00]/30">
                    <ChevronRight className="w-3.5 h-3.5 text-[#FF4D00]" />
                    <span className="text-xs font-['Barlow_Condensed'] font-semibold">Phase {msg.data.phase}: {msg.data.phase_name}</span>
                  </div>
                </motion.div>
              );
            }
            if (msg.type === 'message') {
              const voice = voices.find(v => v.name === msg.data.voice);
              const isHot = msg.data.moment_tags?.includes('hot_take');
              return (
                <motion.div key={`m-${i}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`mb-2 p-3 bg-[#1A1A1A] border ${isHot ? 'border-[#FF4D00]/50' : 'border-[#2A2A2A]'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-4 h-4 flex items-center justify-center border text-[7px] font-bold"
                      style={{ borderColor: voice?.color, color: voice?.color }}>{msg.data.voice?.charAt(0)}</div>
                    <span className="text-[10px] font-medium" style={{ color: voice?.color }}>{msg.data.voice}</span>
                    {isHot && <span className="text-[8px] px-1 py-0.5 bg-[#FF4D00]/10 text-[#FF4D00] font-mono"><Zap className="w-2.5 h-2.5 inline" /> HOT TAKE</span>}
                  </div>
                  <p className="text-xs text-[#F5F5F5] leading-relaxed whitespace-pre-wrap">{msg.data.text}</p>
                </motion.div>
              );
            }
            if (msg.type === 'verdict') {
              const vc = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };
              return (
                <motion.div key={`v-${i}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="my-6 p-5 bg-[#1A1A1A] border-2 text-center" style={{ borderColor: vc[msg.data.verdict] || '#FF4D00' }}>
                  <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">FINAL CALL</span>
                  <span className="text-3xl font-bold font-['Barlow_Condensed']" style={{ color: vc[msg.data.verdict] || '#FF4D00' }}>{msg.data.verdict}</span>
                  <p className="text-xs text-[#A0A0A0] mt-2 max-w-md mx-auto">{msg.data.executive_summary}</p>
                </motion.div>
              );
            }
            return null;
          })}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Timeline & Controls */}
      <div className="border-t border-[#2A2A2A] shrink-0">
        {/* Moment markers */}
        <div className="h-8 relative bg-[#1A1A1A] mx-3 md:mx-4 mt-2">
          <div className="absolute inset-0">
            {/* Progress bar */}
            <div className="h-full bg-[#FF4D00]/10 relative">
              <div className="h-full bg-[#FF4D00]/20" style={{ width: `${progress}%` }} />
            </div>
          </div>
          {/* Moment dots */}
          {data.moments?.map((m, i) => {
            const pos = data.events?.length ? (data.events.findIndex(e => e.sequence_id >= m.seq) / data.events.length) * 100 : 0;
            return (
              <button
                key={i}
                onClick={() => jumpToMoment(m.seq)}
                className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border border-black hover:scale-150 transition-transform"
                style={{ left: `${pos}%`, backgroundColor: MOMENT_COLORS[m.type] || '#FF4D00' }}
                title={m.label}
                data-testid={`moment-${i}`}
              />
            );
          })}
        </div>

        {/* Moment rail */}
        <div className="flex gap-2 px-3 md:px-4 py-2 overflow-x-auto">
          {data.moments?.map((m, i) => (
            <button
              key={i}
              onClick={() => jumpToMoment(m.seq)}
              className="text-[8px] px-2 py-1 border border-[#2A2A2A] text-[#A0A0A0] font-mono shrink-0 hover:border-[#FF4D00]/40 hover:text-[#FF4D00] transition-colors"
              data-testid={`moment-chip-${i}`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: MOMENT_COLORS[m.type] || '#FF4D00' }} />
              {m.label}
            </button>
          ))}
        </div>

        {/* Playback controls */}
        <div className="flex items-center justify-between px-3 md:px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setPlaying(!playing)}
              className="w-10 h-10 md:w-8 md:h-8 flex items-center justify-center bg-[#FF4D00] text-black" data-testid="play-pause">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={reset} className="px-2 py-1 min-h-[44px] md:min-h-0 text-[9px] font-mono text-[#A0A0A0] border border-[#2A2A2A]" data-testid="reset-replay">Reset</button>
          </div>
          <div className="flex items-center gap-1">
            {[1, 1.5, 2, 4].map(s => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-2 py-1 min-h-[44px] md:min-h-0 min-w-[44px] md:min-w-0 text-[9px] font-mono border transition-colors ${
                  speed === s ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'
                }`}
                data-testid={`speed-${s}`}
              >{s}x</button>
            ))}
          </div>
          <span className="text-[9px] font-mono text-[#A0A0A0]">
            {currentIdx}/{data.events?.length || 0} events
          </span>
        </div>
      </div>
    </div>
  );
}
